import axios from 'axios';

// TODO: the forum API is really inconsistent as to when the userId is required.
//
// Technically, it's never really required, since we can pull it from the
// authorization token server-side.
//
// I think this originally got introduced when it came to designing the API for
// editing post likes:
//
//   PUT /forum-threads/:threadId/posts/:postId/likes/:userId
//   DEL /forum-threads/:threadId/posts/:postId/likes/:userId
//
// The goal was to allow PUT semantics for likes (ie. create the like if it
// exists, ignore it otherwise). However, we could have just as easily left out
// the userId:
//
//   PUT /forum-threads/:threadId/posts/:postId/like
//   DEL /forum-threads/:threadId/posts/:postId/like
//
// If I were to redo things, I would opt for something like that instead. I'm
// sure other bits of the API could use cleanup like that as well.
//
// Also, while a very minor note, it might look cleaner with a top-level /forum/
// part for all routes:
//
//   GET /forum/threads
//   GET /forum/notifications
//   DEL /forum/threads/:threadId/posts/:postId/like
//   ...
//

/**
 * @typedef {object} ForumCategory
 * @property {number} id
 * @property {string} name
 * @property {string} label
 * @property {string} bannerUrl
 */

/**
 * @typedef {object} ForumUser
 * @property {number}   id
 * @property {string}   name
 * @property {string[]} roles
 * @property {object}   [currentTerm]
 * @property {number}   currentTerm.id
 * @property {string}   currentTerm.name
 * @property {string}   currentTerm.type
 * @property {string[]} termIds
 * @property {string}   avatarUrl
 */

/**
 * @typedef {object} ForumThread
 * @property {number}        id
 * @property {ForumCategory} category
 * @property {ForumUser}     author
 * @property {object}        firstPost
 * @property {number}        firstPost.id
 * @property {Date}          firstPost.createdAt
 * @property {object}        lastPost
 * @property {number}        lastPost.id
 * @property {Date}          lastPost.createdAt
 * @property {object}        [answerPost]
 * @property {number}        answerPost.id
 * @property {Date}          answerPost.createdAt
 * @property {string}        title
 * @property {boolean}       sticky
 * @property {number}        postCount
 * @property {number}        viewCount
 * @property {number}        likeCount
 * @property {number}        userCount
 * @property {string[]}      termTypes
 * @property {Path[]}        paths - see services/paths
 * @property {Assignment[]}  assignments - see services/assignments
 * @property {ForumTag[]}    tags
 */

/**
 * @typedef {object} ForumThreadTimeline
 * @property {number}   threadId
 * @property {object[]} posts
 * @property {number}   posts[].id
 * @property {Date}     posts[].createdAt
 */

/**
 * @typedef {object} ForumPost
 * @property {number}    id
 * @property {number}    threadId
 * @property {ForumUser} [author] - null if deleted
 * @property {string}    [content] - null if deleted
 * @property {Date}      createdAt
 * @property {Date}      updatedAt
 * @property {Date}      [deletedAt]
 */

/**
 * @typedef {object} ForumPostSearchResult
 * @property {number}    id
 * @property {ForumUser} author
 * @property {object}    thread
 * @property {number}    thread.id
 * @property {string}    thread.title
 * @property {string[]}  excerpts - plain text with <em>...</em> within
 * @property {Date}      createdAt
 * @property {Date}      updatedAt
 */

/**
 * @typedef ForumPostLike
 * @property {number} threadId
 * @property {number} postId
 * @property {number} authorId
 * @property {Date}   createdAt
 */

/**
 * @typedef {object} ForumNotification
 * @property {number}      id
 * @property {string}      kind         - thread-reply | post-reply
 * @property {ForumThread} [thread]     - if thread-reply or post-reply
 * @property {ForumPost}   [post]       - if thread-reply or post-reply
 * @property {ForumPost}   [sourcePost] - if post-reply
 * @property {Date}        [readAt]
 */

/**
 * @typedef {object} ForumTag
 * @property {number} id
 * @property {string} name
 */

function makeReqOptions(options) {
    return Object.assign({
        baseURL: window.apiUrl,
        headers: {
            'Authorization': 'Bearer ' + localStorage.token,
        },
    }, options);
}

function normalizeTimestamps(obj) {
    if (obj) {
        if (obj.createdAt) {
            obj.createdAt = new Date(obj.createdAt);
        }
        if (obj.updatedAt) {
            obj.updatedAt = new Date(obj.updatedAt);
        }
        if (obj.deletedAt) {
            obj.deletedAt = new Date(obj.deletedAt);
        }
    }
    return obj;
}

function normalizeThread(thread) {
    normalizeTimestamps(thread);
    normalizeTimestamps(thread.firstPost);
    normalizeTimestamps(thread.lastPost);
    normalizeTimestamps(thread.answerPost);
    return thread;
}

function normalizeThreadTimeline(timeline) {
    timeline.posts.forEach(normalizeTimestamps);
    return timeline;
}

function normalizePost(post) {
    normalizeTimestamps(post);
    return post;
}

function normalizeNotification(notif) {
    if (notif.thread) {
        notif.thread = normalizeThread(notif.thread);
    }
    if (notif.post) {
        notif.post = normalizePost(notif.post);
    }
    if (notif.sourcePost) {
        notif.sourcePost = normalizePost(notif.sourcePost);
    }
    return notif;
}


/**
 * @returns {ForumCategory[]}
 */
export async function getCategories() {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: '/forum-categories-new',
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return res.data.categories;
}


/**
 * @param {number} categoryId
 * @returns {ForumCategory}
 */
export async function getCategory(categoryId) {
    // TODO: request just the one category from the server
    return (await getCategories()).find(cat => cat.id === categoryId);
}


/**
 * @param {object}   params
 * @param {number}   params.categoryId
 * @param {string}   params.title
 * @param {string}   params.content
 * @param {string[]} params.[termTypes]
 * @param {number[]} params.[pathIds]
 * @param {number[]} params.[assignmentIds]
 * @param {string[]} params.[tagNames]
 * @returns {ForumThread}
 */
export async function createThread(params) {
    const reqOptions = makeReqOptions({
        method: 'POST',
        url: '/forum-threads',
        data: {
            categoryId:    params.categoryId,
            title:         params.title,
            content:       params.content,
            termTypes:     params.termTypes || [],
            pathIds:       params.pathIds || [],
            assignmentIds: params.assignmentIds || [],
            tagNames:      params.tagNames || [],
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThread(res.data.thread);
}


/**
 * Thread order should be one of the following:
 *  - searchRank:desc
 *  - searchRank:asc
 *  - createdAt:desc
 *  - createdAt:asc
 *  - postCount:desc
 *  - postCount:asc
 *  - viewCount:desc
 *  - viewCount:asc
 *  - likeCount:desc
 *  - likeCount:asc
 *  - lastPostCreatedAt:desc
 *  - lastPostCreatedAt:asc
 *
 * Anything else will sort by thread id by default, effectively sorting by
 * thread creation time.
 *
 * Thread period can be any of the following:
 *  - week
 *  - month
 *  - semester
 *
 * @param {object}  [params]
 * @param {number}  params.[categoryId]
 * @param {number}  params.[limit]
 * @param {number}  params.[offset]
 * @param {string}  params.[order] - see above
 * @param {string}  params.[period] - see above
 * @param {string}  params.[notPeriod] - return threads not in this period
 * @param {boolean} params.[sticky]
 * @returns {object}        obj
 * @returns {ForumThread[]} obj.threads
 * @returns {number}        obj.count
 */
export async function getThreadsAndCount(params) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: '/forum-threads',
        params: {
            categoryId: params && params.categoryId,
            limit:      params && params.limit,
            offset:     params && params.offset,
            order:      params && params.order,
            period:     params && params.period,
            notPeriod:  params && params.notPeriod,
            sticky:     params && params.sticky,
            //cache:      'forumthreads'
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.threads.forEach(normalizeThread);
    return {
        threads: res.data.threads,
        count: res.data.totalThreadCount,
    };
}


/**
 * @param {string}   searchText
 * @param {object}   [params]
 * @param {number[]} params.[categoryIds]
 * @param {string[]} params.[termTypes]
 * @param {number[]} params.[pathIds]
 * @param {number[]} params.[assignmentIds]
 * @param {number}   params.[limit]
 * @param {number}   params.[offset]
 * @param {string}   params.[order] - see getThreadsAndCount()
 * @returns {object}        obj
 * @returns {ForumThread[]} obj.threads
 * @returns {number}        obj.count - total thread count
 */
export async function searchThreads(searchText, params) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-threads`,
        params: {
            q:             searchText,
            categoryIds:   params && params.categoryIds,
            termTypes:     params && params.termTypes,
            pathIds:       params && params.pathIds,
            assignmentIds: params && params.assignmentIds,
            limit:         params && params.limit,
            offset:        params && params.offset,
            order:         params && params.order,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.threads.forEach(normalizeThread);
    return {
        threads: res.data.threads,
        count: res.data.totalThreadCount,
    };
}


/**
 * @param {number} threadId
 * @returns {ForumThread}
 */
export async function getThread(threadId) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-threads/${threadId}`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThread(res.data.thread);
}


/**
 * @param {number} threadId
 * @returns {ForumThreadTimeline}
 */
export async function getThreadTimeline(threadId) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-threads/${threadId}/timeline`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThreadTimeline(res.data.timeline);
}


/**
 * @param {number}   threadId
 * @param {object}   params
 * @param {number}   params.[categoryId]
 * @param {string}   params.[title]
 * @param {string[]} params.[termTypes]
 * @param {number[]} params.[pathIds]
 * @param {number[]} params.[assignmentIds]
 * @param {string[]} params.[tagNames]
 * @param {boolean}  params.[sticky]
 */
export async function updateThread(threadId, params) {
    const reqOptions = makeReqOptions({
        method: 'PATCH',
        url: `/forum-threads/${threadId}`,
        data: {
            categoryId:    params.categoryId,
            title:         params.title,
            termTypes:     params.termTypes,
            pathIds:       params.pathIds,
            assignmentIds: params.assignmentIds,
            tagNames:      params.tagNames,
            sticky:        params.sticky,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThread(res.data.thread);
}


/**
 * TODO: replace with updateThread()
 *
 * @param {number} threadId
 * @param {number} answerPostId
 * @returns {ForumThread}
 */
export async function solveThread(threadId, answerPostId) {
    const reqOptions = makeReqOptions({
        method: 'PATCH',
        url: `/forum-threads/${threadId}`,
        data: {
            answerPostId,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThread(res.data.thread);
}


/**
 * TODO: replace with updateThread()
 *
 * @param {number} threadId
 * @returns {ForumThread}
 */
export async function unsolveThread(threadId) {
    const reqOptions = makeReqOptions({
        method: 'PATCH',
        url: `/forum-threads/${threadId}`,
        data: {
            answerPostId: null,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizeThread(res.data.thread);
}


/**
 * @param {number} threadId
 * @param {object} params
 * @param {string} params.content
 * @param {string} params.[sourcePostId]
 * @returns {ForumPost}
 */
export async function createPost(threadId, params) {

    const reqOptions = makeReqOptions({
        method: 'POST',
        url: `/forum-threads/${threadId}/posts`,
        data: {
            content: params.content,
            sourcePostId: params.sourcePostId,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizePost(res.data.post);
}


/**
 * @param {number} threadId
 * @param {number} postId
 * @param {object} params
 * @param {string} params.content
 * @returns {ForumPost}
 */
export async function updatePost(threadId, postId, params) {
    const reqOptions = makeReqOptions({
        method: 'PATCH',
        url: `/forum-threads/${threadId}/posts/${postId}`,
        data: {
            content: params.content,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizePost(res.data.post);
}


/**
 * @param {number} threadId
 * @param {number} postId
 * @returns {ForumPost}
 */
export async function deletePost(threadId, postId) {
    const reqOptions = makeReqOptions({
        method: 'DELETE',
        url: `/forum-threads/${threadId}/posts/${postId}`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return normalizePost(res.data.post);
}


/**
 * @param {object} [params]
 * @param {number} params.[threadId]
 * @param {number} params.[authorId]
 * @param {number} params.[limit]
 * @param {number} params.[offset]
 * @returns {object}      obj
 * @returns {ForumPost[]} obj.posts
 * @returns {number}      obj.count - total post count
 */
export async function getPostsAndCount(params) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-posts`,
        params: {
            threadId: params && params.threadId,
            authorId: params && params.authorId,
            limit:    params && params.limit,
            offset:   params && params.offset,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.posts.forEach(normalizePost);
    return {
        posts: res.data.posts,
        count: res.data.totalPostCount,
    };
}


/**
 * Post order should be one of the following:
 *  - searchRank:desc
 *  - searchRank:asc
 *  - createdAt:desc
 *  - createdAt:asc
 *  - likeCount:desc
 *  - likeCount:asc
 *
 * Anything else will sort by post id by default, effectively sorting by post
 * creation time.
 *
 * @param {string}   searchText
 * @param {object}   [params]
 * @param {number}   params.[authorId]
 * @param {number[]} params.[categoryIds]
 * @param {string[]} params.[termTypes]
 * @param {number[]} params.[pathIds]
 * @param {number[]} params.[assignmentIds]
 * @param {number}   params.[limit]
 * @param {number}   params.[offset]
 * @param {number}   params.[order]
 * @returns {object}                  obj
 * @returns {ForumPostSearchResult[]} obj.posts
 * @returns {number}                  obj.count - total post count
 */
export async function searchPosts(searchText, params) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-posts`,
        params: {
            q:             searchText,
            authorId:      params && params.authorId,
            categoryIds:   params && params.categoryIds,
            termTypes:     params && params.termTypes,
            pathIds:       params && params.pathIds,
            assignmentIds: params && params.assignmentIds,
            limit:         params && params.limit,
            offset:        params && params.offset,
            order:         params && params.order,
            //cache:         'forumposts'
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.posts.forEach(normalizePost);
    return {
        posts: res.data.posts,
        count: res.data.totalPostCount,
    };
}


/**
 * @param {number} threadId
 * @throws {Error}   error
 * @throws {boolean} error.viewExists
 */
export async function createThreadView(threadId) {
    const reqOptions = makeReqOptions({
        method: 'POST',
        url: `/forum-threads/${threadId}/views`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        const err = new Error(res.data.error);
        err.viewExists = !!res.data.viewExists;
        throw err;
    }
}


/**
 * This will clear any related notifications.
 *
 * @param {number} threadId
 * @param {number} postId
 */
export async function createPostView(threadId, postId) {
    const reqOptions = makeReqOptions({
        method: 'POST',
        url: `/forum-threads/${threadId}/posts/${postId}/views`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
}


/**
 * @param {number} threadId
 * @param {number} postId
 * @param {number} userId
 */
export async function likePost(threadId, postId, userId) {
    const reqOptions = makeReqOptions({
        method: 'PUT',
        url: `/forum-threads/${threadId}/posts/${postId}/likes/${userId}`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
}


/**
 * @param {number} threadId
 * @param {number} postId
 * @param {number} userId
 */
export async function unlikePost(threadId, postId, userId) {
    const reqOptions = makeReqOptions({
        method: 'DELETE',
        url: `/forum-threads/${threadId}/posts/${postId}/likes/${userId}`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
}


/**
 * Returns the post likes created by the currently logged in user for a
 * particular thread.
 *
 * @param {number} threadId
 * @returns {ForumPostLike[]}
 */
export async function getLikedPosts(threadId) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-threads/${threadId}/post-likes`,
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.postLikes.forEach(normalizeTimestamps);
    return res.data.postLikes;
}


/**
 * @param {number}  userId
 * @param {object}  [params]
 * @param {boolean} params.[read] - false == only unread notifications
 * @param {number}  params.[limit]
 * @param {number}  params.[offset]
 */
export async function getNotificationsForUser(userId, params) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-users/${userId}/notifications`,
        params: {
            read:   params && params.read,
            limit:  params && params.limit,
            offset: params && params.offset,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    res.data.notifications.forEach(normalizeNotification);
    return res.data.notifications;
}


/**
 * @param {string} searchTerm
 * @returns {ForumTag[]}
 */
export async function searchTags(searchTerm) {
    const reqOptions = makeReqOptions({
        method: 'GET',
        url: `/forum-tags`,
        params: {
            q: searchTerm,
        },
    });
    const res = await axios.request(reqOptions);
    if (!res.data.success) {
        throw new Error(res.data.error);
    }
    return res.data.tags;
}


export default {
    // Categories
    getCategories,
    getCategory,

    // Threads
    createThread,
    getThreadsAndCount,
    searchThreads,
    getThread,
    getThreadTimeline,
    updateThread,
    solveThread,
    unsolveThread,

    // Posts
    createPost,
    updatePost,
    deletePost,
    getPostsAndCount,
    searchPosts,

    // Views
    createThreadView,
    createPostView,

    // Likes
    likePost,
    unlikePost,
    getLikedPosts,

    // Notifications
    getNotificationsForUser,

    // Tags
    searchTags,
};




// WEBPACK FOOTER //
// ./src/services/forum/index.js